import React, { useCallback, useEffect, useRef, useState } from 'react';
import DropZoneUploader from '../components/dropzone/DropZoneUploader';
import PipelineTracer from '../components/dropzone/PipelineTracer';
import AnalysisOverlay from '../components/dropzone/AnalysisOverlay';
import GameHistory from '../components/dropzone/GameHistory';
import {
  triggerAnalysis,
  getJobStatus,
  subscribeToProgress,
  uploadVideoToGcs,
  uploadFilesToGcs,
  getGameHistory,
  saveGameToHistory,
} from '../utils/dropZoneApi';
import { toast } from '../components/ui/use-toast';

const TERMINAL_STATUSES = new Set(['complete', 'error']);
const INITIAL_FORM_DATA = {
  gcsUri: '',
  gameName: '',
  homeTeam: '',
  awayTeam: '',
  gameDate: '',
};

function getErrorMessage(error) {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Unexpected Drop Zone error'
  );
}

export default function DropZonePage() {
  const [currentJob, setCurrentJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [formData, setFormData] = useState(() => ({
    ...INITIAL_FORM_DATA,
    gameDate: new Date().toISOString().slice(0, 10),
  }));
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [overlayGameId, setOverlayGameId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const previousStatusRef = useRef(null);
  const progressSubscriptionRef = useRef(null);

  const buildGameRecord = useCallback((source = {}) => {
    const base = currentJob || {};
    const jobId = source.id || source.jobId || base.jobId || null;

    return {
      jobId,
      gameName: source.gameName || base.gameName || formData.gameName || 'Untitled Game',
      homeTeam: source.homeTeam || base.homeTeam || formData.homeTeam || '',
      awayTeam: source.awayTeam || base.awayTeam || formData.awayTeam || '',
      gameDate: source.gameDate || base.gameDate || formData.gameDate || '',
      gcsUri: source.gcsUri || base.gcsUri || formData.gcsUri || '',
      status: source.status || base.status || 'submitted',
      progress: typeof source.progress === 'number' ? source.progress : base.progress || 0,
      message: source.message || base.message || '',
      pipelineResponse: source.pipelineResponse || base.pipelineResponse || null,
      agentTrace: Array.isArray(source.agentTrace) ? source.agentTrace : base.agentTrace || [],
      createdAt: source.createdAt || base.createdAt || new Date().toISOString(),
      updatedAt: source.updatedAt || new Date().toISOString(),
    };
  }, [currentJob, formData]);

  function persistGame(game) {
    if (!game?.jobId) {
      return;
    }

    console.log('[DropZone] persistGame', {
      jobId: game.jobId,
      status: game.status,
      progress: game.progress,
    });

    const nextGames = saveGameToHistory(game);
    setGames(nextGames);
  }

  useEffect(() => {
    const history = getGameHistory();
    console.log('[DropZone] initialize history', { count: history.length });
    setGames(history);

    if (history[0]?.jobId) {
      setSelectedGameId(history[0].jobId);
    }
  }, []);

  useEffect(() => {
    if (!currentJob?.jobId || !jobStatus) {
      return;
    }

    const snapshot = buildGameRecord(jobStatus);
    console.log('[DropZone] sync job state', snapshot);
    persistGame(snapshot);

    if (!selectedGameId || selectedGameId === snapshot.jobId) {
      setSelectedGameId(snapshot.jobId);
    }

    if (snapshot.status === 'complete') {
      setOverlayGameId(snapshot.jobId);
    }
  }, [currentJob, jobStatus, selectedGameId]);

  useEffect(() => {
    if (!currentJob?.jobId || !jobStatus?.status || jobStatus.id !== currentJob.jobId) {
      return;
    }

    if (previousStatusRef.current === jobStatus.status) {
      return;
    }

    console.log('[DropZone] state transition', {
      jobId: currentJob.jobId,
      from: previousStatusRef.current,
      to: jobStatus.status,
      progress: jobStatus.progress,
      message: jobStatus.message,
    });

    const toastMap = {
      submitted: {
        title: 'Analysis submitted',
        description: `${currentJob.gameName || 'Game film'} entered the Drop Zone queue.`,
      },
      triggering: {
        title: 'Pipeline triggering',
        description: 'SLAM Sports AI is handing the video to the analysis pipeline.',
      },
      processing: {
        title: 'Processing film',
        description: 'Segmentation, six-lens analysis, and player cards are in progress.',
      },
      complete: {
        title: 'Analysis complete',
        description: `${currentJob.gameName || 'Game film'} is ready for review.`,
      },
      error: {
        title: 'Analysis error',
        description: jobStatus.message || 'The pipeline reported an error.',
      },
    };

    if (toastMap[jobStatus.status]) {
      toast(toastMap[jobStatus.status]);
    }

    previousStatusRef.current = jobStatus.status;
  }, [currentJob, jobStatus]);

  useEffect(() => {
    if (!currentJob?.jobId || TERMINAL_STATUSES.has(jobStatus?.status)) {
      return undefined;
    }

    let cancelled = false;

    const pollStatus = async () => {
      try {
        console.log('[DropZone] polling job status', { jobId: currentJob.jobId });
        const nextStatus = await getJobStatus(currentJob.jobId);
        if (cancelled) {
          return;
        }

        console.log('[DropZone] polling job status result', nextStatus);
        setJobStatus((previous) => ({
          ...previous,
          ...nextStatus,
        }));
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('[DropZone] polling job status error', error);
      }
    };

    pollStatus();
    const intervalId = window.setInterval(pollStatus, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentJob, jobStatus?.status]);

  useEffect(() => {
    if (!currentJob?.jobId) {
      return undefined;
    }

    if (progressSubscriptionRef.current) {
      progressSubscriptionRef.current.close();
      progressSubscriptionRef.current = null;
    }

    console.log('[DropZone] opening progress stream', { jobId: currentJob.jobId });

    const subscription = subscribeToProgress(currentJob.jobId, async (event) => {
      console.log('[DropZone] progress event', event);

      setJobStatus((previous) => ({
        ...(previous || {}),
        id: previous?.id || currentJob.jobId,
        status: event.status || previous?.status || 'submitted',
        progress: typeof event.progress === 'number' ? event.progress : previous?.progress || 0,
        message: event.message || previous?.message || '',
      }));

      if (event.type === 'complete' || event.type === 'error') {
        try {
          const latestStatus = await getJobStatus(currentJob.jobId);
          console.log('[DropZone] fetched terminal job status', latestStatus);
          setJobStatus((previous) => ({
            ...previous,
            ...latestStatus,
          }));
        } catch (error) {
          console.error('[DropZone] terminal refresh error', error);
        }
      }
    });

    progressSubscriptionRef.current = subscription;

    return () => {
      console.log('[DropZone] closing progress stream', { jobId: currentJob.jobId });
      subscription.close();

      if (progressSubscriptionRef.current === subscription) {
        progressSubscriptionRef.current = null;
      }
    };
  }, [currentJob]);

  async function handleFileSelected(file) {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      console.log('[DropZone] starting single upload', { name: file.name, size: file.size });

      const uploadResult = await uploadVideoToGcs(file, (progress) => {
        setUploadProgress(progress);
      });

      console.log('[DropZone] upload complete', uploadResult);
      setFormData((previous) => ({
        ...previous,
        gcsUri: uploadResult.gcsUri,
      }));

      toast({
        title: 'Upload complete',
        description: `${uploadResult.filename} is in cloud storage and ready for analysis.`,
      });
    } catch (error) {
      const message = getErrorMessage(error);
      console.error('[DropZone] upload error', error);
      toast({
        title: 'Upload failed',
        description: message,
      });
    } finally {
      setIsUploading(false);
    }
  }

  async function handleFilesSelected(files) {
    if (!files || files.length === 0) return;

    if (files.length === 1) {
      handleFileSelected(files[0]);
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      console.log('[DropZone] starting batch upload', {
        count: files.length,
        names: files.map((f) => f.name),
        totalSize: files.reduce((sum, f) => sum + f.size, 0),
      });

      const uploadResult = await uploadFilesToGcs(files, (progress) => {
        setUploadProgress(progress);
      });

      console.log('[DropZone] batch upload complete', uploadResult);
      setFormData((previous) => ({
        ...previous,
        gcsUri: uploadResult.gcsUri,
      }));

      const videoCount = uploadResult.videos?.length || 0;
      const xmlCount = uploadResult.xmlFiles?.length || 0;
      const parts = [];
      if (videoCount > 0) parts.push(`${videoCount} video${videoCount !== 1 ? 's' : ''}`);
      if (xmlCount > 0) parts.push(`${xmlCount} XML file${xmlCount !== 1 ? 's' : ''}`);

      toast({
        title: 'Batch upload complete',
        description: `${parts.join(' and ')} uploaded to cloud storage (session ${uploadResult.sessionId}).`,
      });

      if (uploadResult.errors?.length > 0) {
        toast({
          title: 'Some files failed',
          description: uploadResult.errors.map((e) => e.file).join(', '),
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      console.error('[DropZone] batch upload error', error);
      toast({
        title: 'Upload failed',
        description: message,
      });
    } finally {
      setIsUploading(false);
    }
  }

  function handleWeTransferUrl(url) {
    console.log('[DropZone] WeTransfer URL received', { url });
    toast({
      title: 'WeTransfer link received',
      description: 'WeTransfer download and extraction is coming soon. For now, download the files locally and use folder upload.',
    });
  }

  function handleGcsUri(gcsUri) {
    console.log('[DropZone] set gcsUri', { gcsUri });
    setFormData((previous) => ({
      ...previous,
      gcsUri,
    }));
  }

  async function handleAnalyze() {
    if (!formData.gcsUri) {
      toast({
        title: 'No files uploaded',
        description: 'Upload files or paste a gs:// URI before starting analysis.',
      });
      return;
    }

    if (!formData.gcsUri.startsWith('gs://')) {
      toast({
        title: 'Invalid GCS URI',
        description: 'Use a gs:// path or upload files into Drop Zone first.',
      });
      return;
    }

    try {
      console.log('[DropZone] trigger analyze', formData);
      previousStatusRef.current = null;
      setOverlayGameId(null);

      const response = await triggerAnalysis(formData);
      const submittedJob = {
        jobId: response.jobId,
        status: response.status,
        progress: 0,
        message: 'Job submitted, triggering pipeline...',
        ...formData,
        agentTrace: [],
        pipelineResponse: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      console.log('[DropZone] currentJob created', submittedJob);
      setCurrentJob(submittedJob);
      setJobStatus({
        id: response.jobId,
        ...submittedJob,
      });
      setSelectedGameId(response.jobId);
      persistGame(submittedJob);
    } catch (error) {
      const message = getErrorMessage(error);
      console.error('[DropZone] trigger analyze error', error);
      toast({
        title: 'Unable to start analysis',
        description: message,
      });
    }
  }

  async function handleSelectGame(game) {
    const gameId = game.jobId || game.id;
    console.log('[DropZone] select history game', { gameId, status: game.status });

    setSelectedGameId(gameId);
    setOverlayGameId(game.status === 'complete' ? gameId : null);

    if (!gameId || TERMINAL_STATUSES.has(game.status) || gameId === currentJob?.jobId) {
      return;
    }

    try {
      const latestStatus = await getJobStatus(gameId);
      const updatedGame = {
        ...game,
        ...latestStatus,
        jobId: latestStatus.id || gameId,
      };

      console.log('[DropZone] refreshed history game', updatedGame);
      persistGame(updatedGame);
    } catch (error) {
      console.error('[DropZone] refresh history game error', error);
    }
  }

  const selectedGame =
    games.find((game) => game.jobId === selectedGameId || game.id === selectedGameId) || null;

  const activeTracerGame =
    currentJob?.jobId && jobStatus && !TERMINAL_STATUSES.has(jobStatus.status)
      ? buildGameRecord(jobStatus)
      : selectedGame && !TERMINAL_STATUSES.has(selectedGame.status)
        ? selectedGame
        : null;

  const overlayGame =
    currentJob?.jobId === overlayGameId && jobStatus?.status === 'complete'
      ? buildGameRecord(jobStatus)
      : games.find((game) => game.jobId === overlayGameId || game.id === overlayGameId) || null;

  const isAnalyzing =
    currentJob?.jobId && jobStatus?.status && !TERMINAL_STATUSES.has(jobStatus.status);

  return (
    <div className="h-full min-h-0 bg-[#000000] text-white">
      <div className="grid h-full min-h-0 grid-cols-1 gap-6 p-4 lg:grid-cols-[minmax(0,7fr)_minmax(320px,3fr)] lg:p-6">
        <div className="min-h-0 overflow-y-auto">
          <div className="space-y-6 pb-6">
            <div className="rounded-3xl border border-[#1A1A1A] bg-[#0A0A0A] p-6">
              <div className="text-xs uppercase tracking-[0.24em] text-zinc-500">
                Drop Zone
              </div>
              <div className="mt-2 text-3xl font-semibold text-white">
                Upload game film into the SLAM six-lens pipeline
              </div>
              <div className="mt-3 max-w-3xl text-sm leading-7 text-zinc-400">
                Upload game folders containing paired video + XML files, drop individual files, or paste a
                <span className="mx-1 rounded bg-[#111111] px-2 py-1 font-mono text-xs text-[#E8B800]">
                  gs://
                </span>
                URI. Supports MP4, MOV, AVI, MKV, XML metadata, and ZIP archives up to 5GB per file. Add game metadata, then launch SLAM Sports AI segmentation, analysis, and player-card generation.
              </div>
            </div>

            <DropZoneUploader
              onFilesSelected={handleFilesSelected}
              onFileSelected={handleFileSelected}
              onGcsUri={handleGcsUri}
              onWeTransferUrl={handleWeTransferUrl}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
            />

            <div className="rounded-3xl border border-[#1A1A1A] bg-[#0A0A0A] p-5">
              <div className="mb-2 text-xs text-zinc-600">Optional — metadata will be auto-extracted from uploaded files when available</div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <label className="space-y-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Game Name</span>
                  <input
                    type="text"
                    value={formData.gameName}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        gameName: event.target.value,
                      }))
                    }
                    placeholder="Brown at Harvard"
                    className="w-full rounded-2xl border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E8B800]"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Home Team</span>
                  <input
                    type="text"
                    value={formData.homeTeam}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        homeTeam: event.target.value,
                      }))
                    }
                    placeholder="Harvard"
                    className="w-full rounded-2xl border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E8B800]"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Away Team</span>
                  <input
                    type="text"
                    value={formData.awayTeam}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        awayTeam: event.target.value,
                      }))
                    }
                    placeholder="Brown"
                    className="w-full rounded-2xl border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E8B800]"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Game Date</span>
                  <input
                    type="date"
                    value={formData.gameDate}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        gameDate: event.target.value,
                      }))
                    }
                    className="w-full rounded-2xl border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#E8B800]"
                  />
                </label>
              </div>

              <div className="mt-5 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="rounded-2xl border border-[#1A1A1A] bg-[#111111] px-4 py-3 text-sm text-zinc-400">
                  Source URI:
                  <span className="ml-2 font-mono text-xs text-[#E8B800]">
                    {formData.gcsUri || 'Awaiting upload or pasted gs:// URI'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleAnalyze}
                  disabled={isUploading || isAnalyzing}
                  className={[
                    'inline-flex items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold transition-all duration-200',
                    isUploading || isAnalyzing
                      ? 'cursor-not-allowed border border-[#1A1A1A] bg-[#111111] text-zinc-500'
                      : 'bg-[#E8B800] text-black shadow-[0_0_28px_rgba(232,184,0,0.18)] hover:bg-[#f5c400]',
                  ].join(' ')}
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>
            </div>

            {activeTracerGame ? (
              <PipelineTracer
                jobId={activeTracerGame.jobId}
                status={activeTracerGame.status}
                progress={activeTracerGame.progress}
                agentTrace={activeTracerGame.agentTrace}
                message={activeTracerGame.message}
              />
            ) : null}

            {overlayGame?.pipelineResponse ? (
              <AnalysisOverlay
                pipelineResponse={overlayGame.pipelineResponse}
                gameName={overlayGame.gameName}
                onClose={() => {
                  console.log('[DropZone] close analysis overlay', {
                    jobId: overlayGame.jobId,
                  });
                  setOverlayGameId(null);
                }}
              />
            ) : null}
          </div>
        </div>

        <aside className="min-h-0 overflow-hidden rounded-3xl border border-[#1A1A1A] bg-[#050505]">
          <div className="border-b border-[#1A1A1A] px-5 py-4">
            <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">
              Game History
            </div>
            <div className="mt-1 text-xl font-semibold text-white">
              Recent Drop Zone sessions
            </div>
          </div>

          <div className="h-[calc(100%-88px)] p-4">
            <GameHistory
              games={games}
              onSelectGame={handleSelectGame}
              activeGameId={selectedGameId || currentJob?.jobId}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
